import {
  AttachmentBox_Constraint_,
  AttachmentBox_Update_Column_,
  Attachment_Constraint_,
  Attachment_Update_Column_,
  EsrsAssessmentAnswersAggregateDocument_,
  Esrs_Answer_Constraint_,
  Esrs_Answer_Update_Column_,
  Esrs_DatapointTag_Constraint_,
  Esrs_Datapoint_Insert_Input_,
  GetEsrsMetricAnswerDocument_,
  GetEsrsMetricsDatapointsDocument_,
  GetEsrsMetricsDatapointsQuery_,
  GetMetricAnswerDocumentationByIdDocument_,
  GetSingleEsrsMetricAnswerDocument_,
  ShortUser,
  useGetEsrsMetricsDatapointsQuery,
  useUpsertDatapointMutation,
} from 'models';
import { useMemo } from 'react';
import { GeneratedAnswer } from './MetricsAITypes';
import { useParams } from 'react-router-dom';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { DatapointSourceEnum, TimePeriods, TimePeriodsEnums } from '../../Requirement';
import { MetricsTableData, areArraysOfObjectsEqual } from '../MetricAnswers.hooks';
import { uniq } from 'lodash';
import { AssessableMetrics } from '../Metrics';
import { useToast } from 'utils/hooks';
import { getTagsCombination, NOTAGCOMBINATION } from 'containers/Esrs/utils';

export const omitArrayTypename = (arr: any[]) => arr.map(({ __typename, ...rest }) => rest);

// helpers
const getNestedDataPoints = (metric: MetricsTableData): MetricsTableData[] => {
  const childrenDataPoints: MetricsTableData[] = [];

  const recurseChildren = (row: MetricsTableData) => {
    if (!row.subRows?.length) {
      childrenDataPoints.push(row);
    } else {
      row.subRows.forEach((subRow) => {
        recurseChildren(subRow);
      });
    }
  };

  recurseChildren(metric);

  return childrenDataPoints;
};

const filterUniqueMetricsByNameAndTags = (metrics: MetricsTableData[]): MetricsTableData[] => {
  const uniqueMetrics: MetricsTableData[] = [];
  const uniqueSet = new Set<string>();

  metrics.forEach((metric) => {
    const metricRef = metric.metric.reference;
    const tagsString = metric.tags?.map((tag) => `${tag.tagType} - ${tag.tagValue}`).join(', ');

    const key = `${metricRef}${metric.tags ? ' | ' + tagsString : ''}`;

    if (!uniqueSet.has(key)) {
      uniqueSet.add(key);
      uniqueMetrics.push(metric);
    }
  });

  return uniqueMetrics;
};

export const useMetricAiAnswers = ({
  reportingUnitId,
  quantitativeMetrics,
  narrativeMetrics,
  selectedQuarter,
}: {
  reportingUnitId: string;
  quantitativeMetrics: MetricsTableData[];
  narrativeMetrics: AssessableMetrics;
  selectedQuarter: TimePeriods;
}) => {
  const { esrsAssessmentId = '' } = useParams();
  const flatNestedMetrics = useMemo(
    () => quantitativeMetrics.flatMap((metric) => getNestedDataPoints(metric)),
    [quantitativeMetrics]
  );

  const uniqueNumericMetrics = useMemo(
    () => filterUniqueMetricsByNameAndTags(flatNestedMetrics),
    [flatNestedMetrics]
  );

  const metricRefs = useMemo(() => {
    const quantitativeMetricRefs = uniqueNumericMetrics.map((metric) => metric.metric.reference);
    const narrativeMetricsRefs = narrativeMetrics.map((metric) => metric.reference);
    return uniq([...quantitativeMetricRefs, ...narrativeMetricsRefs]);
  }, [uniqueNumericMetrics, narrativeMetrics]);

  const { data: metricAnswersData } = useGetEsrsMetricsDatapointsQuery({
    variables: {
      assessmentId: esrsAssessmentId,
      metricRefs: metricRefs,
      reportingUnitId: reportingUnitId,
    },
    skip: !esrsAssessmentId || !metricRefs || !reportingUnitId,
  });

  const metricAnswers = useMemo(() => {
    return metricAnswersData?.answers ?? [];
  }, [metricAnswersData]);

  // Find empty datapoints that are not answered (metrics with no answers yet)
  const emptyNumericDataPoints = useMemo(() => {
    return uniqueNumericMetrics.filter((metric) => {
      const matchingAnswer = metricAnswers.find((a) => a.metricRef === metric.metric.reference);

      if (!matchingAnswer) {
        return true;
      }

      const matchingDatapoint = matchingAnswer.datapoints.find(
        (dp) =>
          areArraysOfObjectsEqual(omitArrayTypename(dp.datapointTags), metric.tags) &&
          dp.timeframe === selectedQuarter
      );

      return !matchingDatapoint || !matchingDatapoint.value;
    });
  }, [uniqueNumericMetrics, metricAnswers]);

  const emptyNarrativeDataPoints = useMemo(() => {
    return narrativeMetrics.filter((metric) => {
      const answer = metricAnswers.find((a) => a.metricRef === metric.reference);
      return !answer || !answer.datapoints[0]?.value;
    });
  }, [narrativeMetrics, metricAnswers]);

  const areAnswersGenerated = useMemo(() => {
    return metricAnswers?.some((answer) =>
      answer.datapoints?.some((dp) => dp.source === DatapointSourceEnum.AI)
    );
  }, [metricAnswers]);

  return {
    areAnswersGenerated,
    uniqueNumericMetrics,
    emptyNumericDataPoints,
    emptyNarrativeDataPoints,
    metricAnswers,
  };
};

export const usePopulateMetricAIAnswers = ({
  metricAnswers,
  reportingUnitId,
  selectedQuarter,
}: {
  metricAnswers: GetEsrsMetricsDatapointsQuery_['answers'];
  reportingUnitId?: string;
  selectedQuarter: TimePeriods;
}) => {
  const [upsertDatapoint] = useUpsertDatapointMutation();
  const { user } = useAuth();
  const { esrsAssessmentId = '' } = useParams();
  const toast = useToast();

  const populateNarrativeAnswers = async (
    generatedAnswers: GeneratedAnswer[],
    narrativeMetrics: AssessableMetrics
  ) => {
    try {
      const metricsWithValidAnswers = narrativeMetrics.filter((metric) => {
        const generatedAnswer = generatedAnswers.find((a) => a.metricRef === metric.reference);
        return !!generatedAnswer && !!generatedAnswer.sourceFileId;
      });

      const objects: Esrs_Datapoint_Insert_Input_[] = metricsWithValidAnswers.map((metric) => {
        const currentAnswer = metricAnswers?.find(
          (answer) => answer.metricRef === metric.reference
        );
        const dataPoint = currentAnswer?.datapoints[0];
        const generatedAnswer = generatedAnswers.find((a) => a.metricRef === metric.reference);

        return {
          value: generatedAnswer?.answer ?? '',
          factValue: dataPoint?.factValue,
          timeframe: TimePeriodsEnums.year,
          tagCombination: NOTAGCOMBINATION,
          authorId: user?.id,
          source: DatapointSourceEnum.AI,
          lastEdited: new Date(),
          answer: {
            data: {
              metricRef: currentAnswer?.metricRef ?? metric.reference,
              hasOptedOut: currentAnswer?.hasOptedOut ?? false,
              optOutReason: currentAnswer?.optOutReason ?? '',
              reportingUnitId: reportingUnitId,
              assessmentId: esrsAssessmentId,
              attachmentBox: {
                data: {
                  attachments: {
                    data: [
                      {
                        fileId: generatedAnswer?.sourceFileId,
                        deletedAt: null,
                      },
                    ],
                    on_conflict: {
                      constraint: Attachment_Constraint_.AttachmentAttachmentBoxIdFileIdKey_,
                      update_columns: [Attachment_Update_Column_.DeletedAt_],
                    },
                  },
                },
                on_conflict: {
                  constraint: AttachmentBox_Constraint_.AttachmentBoxMetricAnswerIdKey1_,
                  update_columns: [AttachmentBox_Update_Column_.MetricAnswerId_],
                },
              },
              optOutAuthorId: user?.id,
            },
            on_conflict: {
              constraint: Esrs_Answer_Constraint_.AnswerMetricRefAssessmentIdReportingUnitIdKey_,
              update_columns: [
                Esrs_Answer_Update_Column_.HasOptedOut_,
                Esrs_Answer_Update_Column_.OptOutReason_,
                Esrs_Answer_Update_Column_.OptOutAuthorId_,
              ],
            },
          },
        };
      });

      if (!!metricsWithValidAnswers.length) {
        await upsertDatapoint({
          variables: { objects },
          refetchQueries: [
            GetEsrsMetricAnswerDocument_,
            GetEsrsMetricsDatapointsDocument_,
            GetSingleEsrsMetricAnswerDocument_,
            EsrsAssessmentAnswersAggregateDocument_,
            GetMetricAnswerDocumentationByIdDocument_,
          ],
        });
        toast({
          text: 'Successfully populated narrative answer(s)',
        });
      }
    } catch (error) {
      console.error('Failed to populate narrative answers:', error);
      toast({
        text: 'Failed to populate narrative answer(s)',
        variant: 'danger',
      });
      throw error;
    }
  };

  const populateQuantitativeAnswers = async (
    generatedAnswers: GeneratedAnswer[],
    numericMetrics: MetricsTableData[]
  ) => {
    try {
      const metricsWithValidAnswers = (numericMetrics ?? []).filter(
        (metric) =>
          !!generatedAnswers.find(
            (a) =>
              a.metricRef === metric.metric.reference &&
              areArraysOfObjectsEqual(a.tags, metric.tags)
          )
      );

      const objects: Esrs_Datapoint_Insert_Input_[] =
        metricsWithValidAnswers?.map((metric) => {
          const currentAnswer = metricAnswers?.find(
            (answer) => answer.metricRef === metric.metric.reference
          );

          const generatedAnswer = generatedAnswers.find(
            (a) =>
              a.metricRef === metric.metric.reference &&
              areArraysOfObjectsEqual(a.tags, metric.tags)
          );

          const currentDataPoint = currentAnswer?.datapoints.find(
            (dp) =>
              areArraysOfObjectsEqual(omitArrayTypename(dp.datapointTags), generatedAnswer?.tags) &&
              dp.timeframe === selectedQuarter
          );

          return {
            id: currentDataPoint?.id ?? undefined,
            value: generatedAnswer?.answer ?? '0',
            timeframe: selectedQuarter,
            tagCombination: getTagsCombination(generatedAnswer?.tags ?? []),
            authorId: user?.id,
            source: DatapointSourceEnum.AI,
            lastEdited: new Date(),
            datapointTags: {
              data: generatedAnswer?.tags ?? [],
              on_conflict: {
                constraint:
                  Esrs_DatapointTag_Constraint_.DatapointTagDatapointIdTagValueTagTypeKey_,
              },
            },
            answer: {
              data: {
                metricRef: currentAnswer?.metricRef ?? metric.metric.reference,
                hasOptedOut: currentAnswer?.hasOptedOut ?? false,
                optOutReason: currentAnswer?.optOutReason ?? '',
                reportingUnitId: reportingUnitId,
                assessmentId: esrsAssessmentId,
                attachmentBox: {
                  data: {
                    attachments: {
                      data: [
                        {
                          fileId: generatedAnswer?.sourceFileId,
                          deletedAt: null,
                        },
                      ],
                      on_conflict: {
                        constraint: Attachment_Constraint_.AttachmentAttachmentBoxIdFileIdKey_,
                        update_columns: [Attachment_Update_Column_.DeletedAt_],
                      },
                    },
                  },
                  on_conflict: {
                    constraint: AttachmentBox_Constraint_.AttachmentBoxMetricAnswerIdKey1_,
                    update_columns: [AttachmentBox_Update_Column_.MetricAnswerId_],
                  },
                },
                optOutAuthorId: user?.id,
              },
              on_conflict: {
                constraint: Esrs_Answer_Constraint_.AnswerMetricRefAssessmentIdReportingUnitIdKey_,
                update_columns: [
                  Esrs_Answer_Update_Column_.HasOptedOut_,
                  Esrs_Answer_Update_Column_.OptOutReason_,
                  Esrs_Answer_Update_Column_.OptOutAuthorId_,
                ],
              },
            },
          };
        }) ?? [];

      if (!!metricsWithValidAnswers.length) {
        await upsertDatapoint({
          variables: { objects },
          refetchQueries: [
            GetEsrsMetricAnswerDocument_,
            GetEsrsMetricsDatapointsDocument_,
            GetSingleEsrsMetricAnswerDocument_,
            EsrsAssessmentAnswersAggregateDocument_,
            GetMetricAnswerDocumentationByIdDocument_,
          ],
        });
        toast({
          text: 'Successfully populated quantitative answer(s)',
        });
      }
    } catch (error) {
      console.error('Failed to populate quantitative answers:', error);
      toast({
        text: 'Failed to populate quantitative answer(s)',
        variant: 'danger',
      });
      throw error;
    }
  };

  return { populateQuantitativeAnswers, populateNarrativeAnswers };
};
